import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, MessageCircle, Calendar, Clock, ChevronLeft, Share2, User, ArrowRight, BookOpen, ExternalLink } from 'lucide-react';
import { getCategoryConfig, CategoryBadge, ReadingProgress, KeyTakeaway, BottomLine } from '../components/blog/BlogGraphics';
import SocialShare from '../components/SocialShare';
import SEOHead from '../components/SEOHead';
import RelatedArticlesCards, { extractRelatedLinks, removeRelatedLinksFromContent } from '../components/RelatedArticlesCards';
import {
  CompensationComparisonChart,
  StatuteOfLimitationsTimeline,
  WhyStatutesExistGrid,
  ExceptionsFlowchart,
  CarAccidentStepsFlowchart,
  MaintenanceAndCureInfoGraphic,
  JonesActRightsDiagram,
  FirstResponderDecisionTree,
  DocumentsChecklistGraphic,
  CaseValueFactorsGraphic,
  ClaimsProcessTimeline,
  InsuranceVsAttorneyComparison,
  InsuranceAdjusterTacticsGraphic,
  AfterAccidentInsuranceSteps,
  WashingtonClaimsLawGraphic,
  CrosswalkLawDiagram,
  ComparativeNegligenceChart,
  PedestrianEvidenceChecklist,
  PedestrianClaimTimeline,
  SocialMediaRisksDiagram,
  SocialMediaTimelineImpact,
  SocialMediaProtectionSteps,
  ComparativeFaultExamplesChart,
  PureVsModifiedComparativeFault,
  ComparativeFaultProtectionSteps,
  FirefighterInjuryTypes,
  WorkersCompVsCivilClaim,
  EMSInjuryScenarios,
  WSDOTWorkZoneHazards,
  PublicWorksInjuryChecklist,
  PublicSafetyClaimDecisionTree,
} from '../components/ArticleGraphics';

// Map article slugs to their relevant graphics
const articleGraphicsMap = {
  'should-you-talk-to-insurance-adjuster-washington': [
    { component: InsuranceAdjusterTacticsGraphic, position: 'after-intro', caption: 'Why insurance adjusters push for early recorded statements' },
    { component: WashingtonClaimsLawGraphic, position: 'mid-content', caption: 'Washington law protects your right to decline statements' },
    { component: AfterAccidentInsuranceSteps, position: 'before-cta', caption: 'Step-by-step: What to do instead of giving a statement' },
  ],
  'pedestrian-crosswalk-injury-claims-washington': [
    { component: CrosswalkLawDiagram, position: 'after-intro', caption: 'Washington crosswalk law: When drivers must stop for pedestrians' },
    { component: ComparativeNegligenceChart, position: 'mid-content', caption: 'How comparative negligence affects your recovery' },
    { component: PedestrianEvidenceChecklist, position: 'mid-content', caption: 'Evidence checklist to strengthen your pedestrian claim' },
    { component: PedestrianClaimTimeline, position: 'before-cta', caption: 'Critical deadlines for pedestrian injury claims' },
  ],
  'social-media-destroy-personal-injury-case-washington': [
    { component: SocialMediaRisksDiagram, position: 'after-intro', caption: 'What insurance adjusters actively search for on your profiles' },
    { component: SocialMediaTimelineImpact, position: 'mid-content', caption: 'When social media posts hurt your case most' },
    { component: SocialMediaProtectionSteps, position: 'before-cta', caption: 'Your 8-step social media protection plan' },
  ],
  'why-hire-personal-injury-attorney': [
    { component: CompensationComparisonChart, position: 'after-intro', caption: 'Average compensation with vs. without attorney representation' },
    { component: InsuranceVsAttorneyComparison, position: 'mid-content', caption: 'Comparing DIY claims vs. attorney representation' },
    { component: CaseValueFactorsGraphic, position: 'before-cta', caption: 'Factors that determine your case value' },
  ],
  'why-statutes-of-limitations-exist-washington-personal-injury': [
    { component: StatuteOfLimitationsTimeline, position: 'after-intro', caption: 'Washington\'s 3-year filing deadline for personal injury claims' },
    { component: WhyStatutesExistGrid, position: 'after-intro', caption: 'Key reasons for Washington\'s statute of limitations' },
    { component: ExceptionsFlowchart, position: 'mid-content', caption: 'Exceptions that may extend the filing deadline' },
  ],
  'what-to-do-after-car-accident-washington': [
    { component: CarAccidentStepsFlowchart, position: 'after-intro', caption: 'Step-by-step guide: What to do after a car accident' },
    { component: DocumentsChecklistGraphic, position: 'mid-content', caption: 'Documents to gather for your claim' },
  ],
  'maintenance-and-cure-explained': [
    { component: MaintenanceAndCureInfoGraphic, position: 'after-intro', caption: 'Understanding Maintenance and Cure benefits' },
    { component: JonesActRightsDiagram, position: 'mid-content', caption: 'Your rights under the Jones Act' },
  ],
  'understanding-jones-act-maritime-workers': [
    { component: JonesActRightsDiagram, position: 'after-intro', caption: 'Jones Act protections for maritime workers' },
    { component: MaintenanceAndCureInfoGraphic, position: 'mid-content', caption: 'Maintenance and Cure: Your guaranteed benefits' },
  ],
  'first-responder-injury-rights-washington': [
    { component: FirstResponderDecisionTree, position: 'after-intro', caption: 'Do you have a civil claim beyond workers\' comp?' },
    { component: DocumentsChecklistGraphic, position: 'mid-content', caption: 'Documents to gather for your claim' },
  ],
  'top-personal-injury-lawyers-washington-2026': [
    { component: CaseValueFactorsGraphic, position: 'mid-content', caption: 'What determines your case value' },
    { component: ClaimsProcessTimeline, position: 'before-cta', caption: 'What to expect during your case' },
  ],
  // New public safety sector articles
  'firefighter-injury-claims-washington': [
    { component: FirefighterInjuryTypes, position: 'after-intro', caption: 'Common firefighter third-party injury scenarios' },
    { component: WorkersCompVsCivilClaim, position: 'mid-content', caption: 'Workers\' comp vs. civil claim: what each covers' },
    { component: PublicSafetyClaimDecisionTree, position: 'before-cta', caption: 'Do you have a third-party claim?' },
  ],
  'paramedic-emt-injury-claims-washington': [
    { component: EMSInjuryScenarios, position: 'after-intro', caption: 'When EMS workers can file third-party claims' },
    { component: WorkersCompVsCivilClaim, position: 'mid-content', caption: 'Workers\' comp vs. civil claim compensation' },
    { component: DocumentsChecklistGraphic, position: 'before-cta', caption: 'Documents to gather for your claim' },
  ],
  'wsdot-highway-worker-injury-claims-washington': [
    { component: WSDOTWorkZoneHazards, position: 'after-intro', caption: 'WSDOT and highway worker injury risks' },
    { component: WorkersCompVsCivilClaim, position: 'mid-content', caption: 'Workers\' comp vs. third-party civil claim' },
    { component: PublicSafetyClaimDecisionTree, position: 'before-cta', caption: 'Do you have a third-party claim?' },
  ],
  'public-works-employee-injury-claims-washington': [
    { component: PublicWorksInjuryChecklist, position: 'after-intro', caption: 'Your action checklist after an on-the-job injury' },
    { component: WorkersCompVsCivilClaim, position: 'mid-content', caption: 'Workers\' comp vs. civil claim: what\'s the difference?' },
    { component: PublicSafetyClaimDecisionTree, position: 'before-cta', caption: 'Do you have a third-party claim?' },
  ],
  // Comparative Fault article
  'washington-comparative-fault-law-injury-settlement': [
    { component: ComparativeFaultExamplesChart, position: 'after-intro', caption: 'How comparative fault percentages reduce your recovery' },
    { component: PureVsModifiedComparativeFault, position: 'mid-content', caption: 'Washington\'s pure system vs. other states' },
    { component: ComparativeFaultProtectionSteps, position: 'before-cta', caption: '6 steps to protect your claim from unfair fault reductions' },
  ],
};

// Graphic wrapper component with caption
const ArticleGraphic = ({ component: GraphicComponent, caption }) => (
  <figure className="my-10">
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100 p-4">
      <GraphicComponent />
    </div>
    {caption && (
      <figcaption className="text-center text-sm text-slate-500 mt-3 italic">
        {caption}
      </figcaption>
    )}
  </figure>
);

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [readingProgress, setReadingProgress] = useState(0);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const contentRef = useRef(null);

  // Get graphics for this article
  const articleGraphics = articleGraphicsMap[slug] || [];

  useEffect(() => {
    fetchPost();
    fetchRelatedPosts();
  }, [slug]);

  // Reading progress tracker
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      
      const element = contentRef.current;
      const totalHeight = element.clientHeight - window.innerHeight;
      const windowScrollTop = window.scrollY - element.offsetTop + 200;
      
      if (windowScrollTop <= 0) {
        setReadingProgress(0);
      } else if (windowScrollTop >= totalHeight) {
        setReadingProgress(100);
      } else {
        setReadingProgress((windowScrollTop / totalHeight) * 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
      } else {
        setError('Post not found');
      }
    } catch (err) {
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`);
      const data = await response.json();
      // Get 3 random posts excluding current
      const others = (data.posts || []).filter(p => p.slug !== slug).slice(0, 3);
      setRelatedPosts(others);
    } catch (err) {
      console.error('Failed to fetch related posts:', err);
    }
  };

  const sharePost = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-coral-200 border-t-primary rounded-full animate-spin" />
          <span className="text-slate-500">Loading article...</span>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-slate-50">
        <div className="text-center bg-white p-10 rounded-2xl shadow-lg max-w-md">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-secondary mb-2">Article Not Found</h2>
          <p className="text-slate-500 mb-6">The article you're looking for doesn't exist or has been moved.</p>
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to All Articles
          </Link>
        </div>
      </div>
    );
  }

  const config = getCategoryConfig(post.category);
  const Icon = config.icon;

  // Extract FAQ data from content for schema (if article has FAQ section)
  const extractFAQs = () => {
    const faqs = [];
    const faqPatterns = [
      { q: "Can they deny my claim if I refuse?", a: "No — that's an unfair practice tactic." },
      { q: "How long do I have?", a: "Washington gives 3 years for most personal injury claims (RCW 4.16.080)." },
      { q: "Does it matter if the crosswalk was unmarked?", a: "No — the law still applies at intersections." },
      { q: "What if I was distracted?", a: "It may reduce your recovery but doesn't eliminate it under Washington's comparative negligence rules." },
      { q: "Can they force full account access?", a: "Not automatically — must show relevance under CR 26. Broad requests get denied or limited." },
      { q: "What about private posts?", a: "If relevant, subpoenas can reach them. Assume nothing is truly private." },
    ];
    
    // Check if post content contains these FAQs
    if (post.content) {
      faqPatterns.forEach(faq => {
        if (post.content.includes(faq.q)) {
          faqs.push({ question: faq.q, answer: faq.a });
        }
      });
    }
    return faqs.length > 0 ? faqs : null;
  };

  const faqData = extractFAQs();

  return (
    <>
      {/* SEO Head with Article Schema */}
      <SEOHead 
        title={`${post.title} | Amy C. Brown Law`}
        description={post.excerpt}
        pageType="article"
        image={post.image}
        articleData={{
          title: post.title,
          description: post.excerpt,
          datePublished: post.date,
          dateModified: post.date,
          author: post.author || "Amy C. Brown",
          category: post.category
        }}
        faqData={faqData}
        speakableSelectors={['.article-summary', '.article-content h2', '.article-content p:first-of-type']}
      />
      
    <div className="min-h-screen pt-20" data-testid="blog-post-page" ref={contentRef}>
      {/* Reading Progress Bar */}
      <ReadingProgress progress={readingProgress} />

      {/* Hero Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Link 
            to="/blog"
            className="group inline-flex items-center gap-2 text-slate-500 hover:text-primary mb-8 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to Articles
          </Link>
          
          {/* Category Badge with Icon */}
          <div className="flex items-center gap-4 mb-6">
            <CategoryBadge category={post.category} size="lg" />
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {post.date}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </div>
          
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-secondary mb-8 leading-tight">
            {post.title}
          </h1>
          
          {/* Author & Share Row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-from-prompt-2/artifacts/gra2bbhk_amy-brown-696x696-Logo.jpg"
                alt="Amy C. Brown"
                loading="lazy"
                className="w-14 h-14 rounded-full object-cover border-2 border-slate-200"
              />
              <div>
                <p className="text-secondary font-semibold">{post.author}</p>
                <p className="text-slate-500 text-sm">Personal Injury Attorney</p>
              </div>
            </div>
            
            <button 
              onClick={sharePost}
              className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full hover:bg-slate-200 transition-all"
            >
              <Share2 className="w-4 h-4" />
              Share Article
            </button>
          </div>
        </div>
      </section>

      {/* Article Content - Enhanced Typography */}
      <article className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          {/* Featured Image Card */}
          <div className="relative -mt-20 mb-12 rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src={post.image}
              alt={post.imageAlt}
              loading="lazy"
              className="w-full h-64 md:h-96 object-cover"
            />
            <div className={`absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r ${config.gradient}`} />
          </div>

          {/* Key Takeaway Box at Top */}
          {post.excerpt && (
            <KeyTakeaway title="Article Summary">
              {post.excerpt}
            </KeyTakeaway>
          )}

          {/* Graphic - After Intro Position */}
          {articleGraphics.filter(g => g.position === 'after-intro').map((graphic, idx) => (
            <ArticleGraphic key={`intro-${idx}`} component={graphic.component} caption={graphic.caption} />
          ))}

          {/* Main Content with Enhanced Prose Styling */}
          <div 
            className="prose prose-lg max-w-none 
              prose-headings:font-heading prose-headings:text-secondary prose-headings:font-bold
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-3 prose-h2:border-b prose-h2:border-slate-200
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline
              prose-strong:text-secondary prose-strong:font-semibold
              prose-ul:my-6 prose-li:text-slate-700 prose-li:my-2
              prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-coral-50/50 
              prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:italic
              prose-blockquote:text-slate-700 prose-blockquote:not-italic prose-blockquote:font-normal"
            dangerouslySetInnerHTML={{ __html: removeRelatedLinksFromContent(post.content) }}
          />

          {/* Related Articles Cards - Extracted from content */}
          <RelatedArticlesCards links={extractRelatedLinks(post.content)} />

          {/* Graphic - Mid Content Position */}
          {articleGraphics.filter(g => g.position === 'mid-content').map((graphic, idx) => (
            <ArticleGraphic key={`mid-${idx}`} component={graphic.component} caption={graphic.caption} />
          ))}

          {/* Graphic - Before CTA Position */}
          {articleGraphics.filter(g => g.position === 'before-cta').map((graphic, idx) => (
            <ArticleGraphic key={`cta-${idx}`} component={graphic.component} caption={graphic.caption} />
          ))}

          {/* Author Bio Box - AI Trust Signal */}
          <div className="mt-16 p-8 bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl border border-slate-200" itemScope itemType="https://schema.org/Person">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_site-from-prompt-2/artifacts/gra2bbhk_amy-brown-696x696-Logo.jpg"
                  alt="Amy C. Brown - Personal Injury Attorney"
                  loading="lazy"
                  className="w-28 h-28 rounded-2xl object-cover shadow-lg"
                  itemProp="image"
                />
                <div className={`absolute -bottom-2 -right-2 p-2 bg-gradient-to-br ${config.gradient} rounded-xl shadow-lg`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <p className="text-sm text-primary font-medium mb-1">About the Author</p>
                <h3 className="font-heading text-2xl font-bold text-secondary mb-2" itemProp="name">
                  Amy C. Brown
                </h3>
                <p className="text-slate-600 mb-4 text-sm" itemProp="description">
                  Personal injury attorney with 10+ years of experience, licensed in Washington State. Former defense attorney who now fights for injured individuals. Member of WSBA Solo & Small Practice Section.
                </p>
                <p className="text-slate-600 mb-6 text-lg">
                  Questions about your case? I personally respond to every inquiry.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <a 
                    href="tel:425-675-1224"
                    className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-coral-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all"
                    itemProp="telephone"
                  >
                    <Phone className="w-5 h-5" />
                    (425) 675-1224
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                  <a 
                    href="sms:425-675-1224"
                    className="inline-flex items-center justify-center gap-2 border-2 border-primary text-primary px-6 py-3 rounded-full font-semibold hover:bg-coral-50 transition-all"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Text Me
                  </a>
                </div>
                <meta itemProp="jobTitle" content="Personal Injury Attorney" />
                <meta itemProp="worksFor" content="Amy C. Brown Law" />
              </div>
            </div>
          </div>

          {/* Social Share Section */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-slate-600 font-medium">Found this article helpful?</p>
              <SocialShare title={post.title} url={window.location.href} />
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts Section */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6 max-w-6xl">
            <div className="flex items-center gap-3 mb-10">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
              <h2 className="font-heading text-2xl font-bold text-secondary">More Articles</h2>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => {
                const relatedConfig = getCategoryConfig(relatedPost.category);
                const RelatedIcon = relatedConfig.icon;
                
                return (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all border border-slate-100"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent`} />
                      <div className="absolute top-3 right-3 p-2 bg-white/90 rounded-full">
                        <RelatedIcon className={`w-4 h-4 ${relatedConfig.textColor}`} />
                      </div>
                    </div>
                    <div className="p-5">
                      <CategoryBadge category={relatedPost.category} size="sm" />
                      <h3 className="font-heading text-lg font-semibold text-secondary mt-3 group-hover:text-primary transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="relative py-20 overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient}`} />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }} />
        
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center relative z-10">
          <Icon className="w-12 h-12 text-white/30 mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Need Legal Help?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Get a free, no-obligation consultation. I fight for injured people in Washington and Alaska.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="group inline-flex items-center justify-center gap-2 bg-white text-slate-800 px-8 py-4 rounded-full font-bold hover:bg-slate-100 transition-all shadow-xl"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link 
              to="/blog"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition-all"
            >
              <BookOpen className="w-5 h-5" />
              More Articles
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default BlogPostPage;
